import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import Button from "../../componentes/button/button";
import { salvarBlob } from "../../services/resource/tiInventarioService";
import VisualizadorAnexo, { podeVisualizar } from "./VisualizadorAnexo";
import { formatarDataHora } from "./tiUtils";

function formatarTamanho(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Anexos do ativo: envio de documentos, lista e visualizador (imagem, PDF, vídeo, áudio, texto). */
export default function AnexosAtivoModal({ visible, onHide, service, ativo, tiposDocumento = [], onAlterado }) {
    const [anexos, setAnexos] = useState([]);
    const [selecionadoId, setSelecionadoId] = useState(null);
    const [tipoDocumento, setTipoDocumento] = useState("OUTRO");
    const [descricao, setDescricao] = useState("");
    const [enviando, setEnviando] = useState(false);
    const inputArquivo = useRef(null);

    const ativoId = ativo?.id;

    const carregar = useCallback(async (selecionarPrimeiro = false) => {
        if (!ativoId) return;
        const res = await service.listarAnexos(ativoId);
        const lista = res.data || [];
        setAnexos(lista);
        setSelecionadoId(atual => {
            if (selecionarPrimeiro && lista.length) return lista[0].id;
            return lista.some(a => a.id === atual) ? atual : (lista[0]?.id ?? null);
        });
    }, [ativoId, service]);

    useEffect(() => {
        if (!visible) return;
        setDescricao("");
        setTipoDocumento("OUTRO");
        carregar(true).catch(() => setAnexos([]));
    }, [visible, carregar]);

    const enviar = async (e) => {
        const arquivo = e.target.files?.[0];
        e.target.value = "";
        if (!arquivo) return;
        setEnviando(true);
        try {
            await service.anexar(ativoId, arquivo, tipoDocumento, descricao);
            toast.success("Documento anexado com sucesso!");
            setDescricao("");
            await carregar(true);
            onAlterado?.();
        } catch (err) {
            // mensagem do backend (tipo/tamanho) exibida pelo interceptor
        } finally {
            setEnviando(false);
        }
    };

    const baixar = async (anexo) => {
        try {
            const res = await service.baixarAnexo(anexo.id);
            salvarBlob(res, anexo.nomeOriginal);
        } catch (e) {
            toast.error("Não foi possível baixar o documento.");
        }
    };

    const remover = async (anexo) => {
        if (!window.confirm(`Remover o documento "${anexo.nomeOriginal}"? A remoção ficará registrada no histórico.`)) return;
        await service.excluirAnexo(anexo.id);
        toast.success("Documento removido.");
        await carregar();
        onAlterado?.();
    };

    const selecionado = anexos.find(a => a.id === selecionadoId) || null;

    return (
        <Dialog
            header={`Anexos — ${ativo?.nomenclatura || ativo?.patrimonio || `#${ativoId ?? ""}`}`}
            visible={visible}
            onHide={onHide}
            blockScroll
            className="w-[98%] md:w-[1100px]"
            contentClassName="pt-3"
        >
            <div className="flex flex-wrap items-end gap-2 mb-4 pb-4 border-b border-border">
                <div>
                    <label className="block text-xs text-muted-foreground mb-1">Tipo de documento</label>
                    <select className="border border-border rounded-md px-2 py-1.5 text-sm bg-card" value={tipoDocumento}
                        onChange={e => setTipoDocumento(e.target.value)}>
                        {tiposDocumento.map(t => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
                    </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs text-muted-foreground mb-1">Descrição (opcional)</label>
                    <input className="border border-border rounded-md px-2 py-1.5 text-sm bg-card w-full"
                        value={descricao} onChange={e => setDescricao(e.target.value)} />
                </div>
                <input ref={inputArquivo} type="file" className="hidden" onChange={enviar} />
                <Button label="Anexar documento" iconLeft="fa fa-paperclip" variant="indigo" loading={enviando}
                    onClick={() => inputArquivo.current?.click()} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                <div className="lg:col-span-2">
                    {anexos.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nenhum documento anexado.</p>
                    ) : (
                        <ul className="divide-y divide-border rounded-xl border border-border">
                            {anexos.map(a => (
                                <li key={a.id}
                                    className={`p-3 text-sm ${a.id === selecionadoId ? "bg-primary/5" : ""}`}>
                                    <button className="text-left font-medium text-foreground hover:underline break-all"
                                        onClick={() => setSelecionadoId(a.id)}>
                                        {a.nomeOriginal}
                                    </button>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        {a.tipoDocumentoRotulo} · {formatarTamanho(a.tamanho)} · {a.usuarioNome} · {formatarDataHora(a.dataHora)}
                                        {a.descricao ? ` · ${a.descricao}` : ""}
                                    </div>
                                    <div className="flex gap-3 mt-1.5 text-xs">
                                        <button className="text-primary hover:underline" onClick={() => setSelecionadoId(a.id)}>
                                            {podeVisualizar(a) ? "Visualizar" : "Detalhes"}
                                        </button>
                                        <button className="hover:underline" onClick={() => baixar(a)}>Baixar</button>
                                        <button className="text-destructive hover:underline" onClick={() => remover(a)}>Remover</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="lg:col-span-3 rounded-xl border border-border p-3 min-h-[300px]">
                    <VisualizadorAnexo service={service} anexo={selecionado} />
                </div>
            </div>
        </Dialog>
    );
}
